/**
 * Buscar licitações (D74–D76): contratações com propostas abertas no PNCP, coletadas a cada 3 horas, filtradas por
 * UF, modalidade e palavras-chave (public.buscar_pncp). Filtros salvos, "novas" desde a última visita,
 * itens consultados na hora, cadastro em um clique e "ignorar".
 * D81–D82: filtros por sistema de origem, esfera e datas; ordenação; favoritas ("novas oportunidades") com anotação.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Title, useGetList, useNotify } from "react-admin";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/AddOutlined";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import EstrelaIcon from "@mui/icons-material/Star";
import EstrelaVaziaIcon from "@mui/icons-material/StarBorder";
import NotaIcon from "@mui/icons-material/EditNoteOutlined";
import FiltroIcon from "@mui/icons-material/FilterListOutlined";
import PesquisaIcon from "@mui/icons-material/SearchOutlined";
import InputAdornment from "@mui/material/InputAdornment";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { beneficioDoPncp, lerControlePncp, trechosDestacados } from "@sobral/dominio";
import { useCelular } from "../componentes";
import { formatarDataHora, formatarMoeda, formatarNumero } from "../lib/formato";
import { consultarItens, linkPncp, situacaoFoge, type ItemPncp } from "../lib/pncp";
import { supabase } from "../lib/supabase";
import { UFS } from "./fornecedores";
import { CadastroPeloPncp, cadastrarDoPncp } from "./licitacoes";
import { linhaDoAchado, pesquisarPncp, type AchadoPncp, type SituacaoPesquisa } from "../lib/pncp-pesquisa";
import { textoDaPreparacao } from "./licitacao-leitura";
import { BENEFICIOS } from "./licitacao-itens";

interface Filtro {
  id: string;
  nome: string;
  ufs: string[];
  modalidades: number[];
  palavras: string[];
  esferas: string[];
  sistemas: string[];
  ultima_visita: string | null;
}

/** Esfera do órgão como o PNCP informa. */
const ESFERAS_PNCP = [
  { id: "F", nome: "Federal" },
  { id: "E", nome: "Estadual" },
  { id: "M", nome: "Municipal" },
  { id: "D", nome: "Distrital" },
  { id: "N", nome: "Não informada" },
];
export const nomeEsfera = (e: string | null) => ESFERAS_PNCP.find((x) => x.id === e)?.nome ?? "—";

type Ordem = "relevancia" | "encerramento" | "publicacao" | "uf" | "modalidade" | "esfera" | "valor";
const ORDENS: { id: Ordem; nome: string }[] = [
  { id: "relevancia", nome: "Relevância" },
  { id: "encerramento", nome: "Encerramento (mais próximo)" },
  { id: "publicacao", nome: "Publicação (mais recente)" },
  { id: "uf", nome: "Estado" },
  { id: "modalidade", nome: "Modalidade" },
  { id: "esfera", nome: "Esfera do órgão" },
  { id: "valor", nome: "Valor estimado (maior)" },
];

const tempo = (d: string | null) => (d ? new Date(d).getTime() : Number.POSITIVE_INFINITY);

function ordenar(lista: Resultado[], ordem: Ordem): Resultado[] {
  // Relevância: a ordem que o PNCP devolveu na pesquisa.
  if (ordem === "relevancia") return lista;
  const texto = (a: string | null, b: string | null) => (a ?? "").localeCompare(b ?? "", "pt-BR");
  const porEncerramento = (a: Resultado, b: Resultado) => tempo(a.data_encerramento_proposta) - tempo(b.data_encerramento_proposta);
  return [...lista].sort((a, b) => {
    switch (ordem) {
      case "publicacao":
        // Mais recente primeiro; sem data de publicação vai para o fim.
        return (b.data_publicacao ? new Date(b.data_publicacao).getTime() : 0) - (a.data_publicacao ? new Date(a.data_publicacao).getTime() : 0) || porEncerramento(a, b);
      case "uf":
        return texto(a.uf, b.uf) || texto(a.municipio, b.municipio) || porEncerramento(a, b);
      case "modalidade":
        return texto(a.modalidade_nome, b.modalidade_nome) || porEncerramento(a, b);
      case "esfera":
        return texto(nomeEsfera(a.esfera), nomeEsfera(b.esfera)) || porEncerramento(a, b);
      case "valor":
        return Number(b.valor_total_estimado ?? -1) - Number(a.valor_total_estimado ?? -1) || porEncerramento(a, b);
      default:
        return porEncerramento(a, b);
    }
  });
}

export interface Resultado {
  numero_controle_pncp: string;
  uf: string;
  municipio: string | null;
  orgao_nome: string;
  unidade_nome: string | null;
  modalidade_id: number;
  modalidade_nome: string;
  numero: string;
  objeto: string;
  valor_total_estimado: number | null;
  srp: boolean;
  data_encerramento_proposta: string | null;
  data_publicacao: string | null;
  link_sistema_origem: string | null;
  sistema: string | null;
  esfera: string | null;
  primeira_coleta_em: string;
  palavras_encontradas: string[];
  licitacao_id: string | null;
  ignorada: boolean;
  favorita: boolean;
  anotacao: string | null;
}

type Marcas = Map<string, { licitacao_id: string | null; favorita: boolean; anotacao: string | null; ignorada: boolean }>;

/** Marcações da equipe (captada, nova oportunidade, anotação, ignorada) para os achados da pesquisa. */
async function marcasDe(controles: string[]): Promise<Marcas> {
  const m: Marcas = new Map(controles.map((c) => [c, { licitacao_id: null, favorita: false, anotacao: null, ignorada: false }]));
  if (!controles.length) return m;
  const [l, f, i] = await Promise.all([
    supabase.from("licitacao").select("id, numero_controle_pncp").in("numero_controle_pncp", controles),
    supabase.from("busca_favorita").select("numero_controle_pncp, anotacao").in("numero_controle_pncp", controles),
    supabase.from("busca_ignorada").select("numero_controle_pncp").in("numero_controle_pncp", controles),
  ]);
  const erro = l.error ?? f.error ?? i.error;
  if (erro) throw new Error(`Não foi possível ler as marcações: ${erro.message}`);
  for (const x of (l.data ?? []) as { id: string; numero_controle_pncp: string }[]) {
    const atual = m.get(x.numero_controle_pncp);
    if (atual) atual.licitacao_id = x.id;
  }
  for (const x of (f.data ?? []) as { numero_controle_pncp: string; anotacao: string | null }[]) {
    const atual = m.get(x.numero_controle_pncp);
    if (atual) Object.assign(atual, { favorita: true, anotacao: x.anotacao });
  }
  for (const x of (i.data ?? []) as { numero_controle_pncp: string }[]) {
    const atual = m.get(x.numero_controle_pncp);
    if (atual) atual.ignorada = true;
  }
  return m;
}

/** Achado da pesquisa no formato da lista, para usar o mesmo cartão e as mesmas ações. */
function resultadoDoAchado(a: AchadoPncp, marcas: Marcas): Resultado {
  const linha = linhaDoAchado(a);
  const marca = marcas.get(a.numero_controle_pncp);
  return {
    numero_controle_pncp: linha.numero_controle_pncp,
    uf: linha.uf,
    municipio: linha.municipio,
    orgao_nome: linha.orgao_nome,
    unidade_nome: linha.unidade_nome,
    modalidade_id: linha.modalidade_id,
    modalidade_nome: linha.modalidade_nome,
    numero: linha.numero,
    objeto: linha.objeto,
    valor_total_estimado: linha.valor_total_estimado,
    srp: linha.srp,
    data_encerramento_proposta: linha.data_encerramento_proposta,
    data_publicacao: linha.data_publicacao,
    link_sistema_origem: linha.link_sistema_origem,
    sistema: null,
    esfera: linha.esfera,
    primeira_coleta_em: "",
    palavras_encontradas: [],
    licitacao_id: marca?.licitacao_id ?? null,
    ignorada: marca?.ignorada ?? false,
    favorita: marca?.favorita ?? false,
    anotacao: marca?.anotacao ?? null,
  };
}

interface Coleta {
  id: string;
  iniciada_em: string;
  concluida_em: string | null;
  registros: number | null;
  novas: number | null;
  erro: string | null;
}

function Destacado({ texto, termos }: { texto: string; termos: string[] }) {
  return (
    <>
      {trechosDestacados(texto, termos).map((t, i) =>
        t.destaque ? (
          <Box key={i} component="mark" sx={{ bgcolor: "rgba(0, 176, 179, 0.18)", color: "inherit", fontWeight: 700, px: 0.25, borderRadius: 0.5 }}>
            {t.texto}
          </Box>
        ) : (
          <span key={i}>{t.texto}</span>
        ),
      )}
    </>
  );
}

export function ChipPrazo({ fim }: { fim: string | null }) {
  if (!fim) return <Chip size="small" variant="outlined" label="Prazo não informado" />;
  const data = new Date(fim);
  const inicioDoDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  // Dias de calendário (no horário do navegador) até o encerramento.
  const dias = Math.round((inicioDoDia(data) - inicioDoDia(new Date())) / 86_400_000);
  const hora = data.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const rotulo = dias <= 0 ? `Encerra hoje, ${hora}` : dias === 1 ? `Encerra amanhã, ${hora}` : `Encerra em ${dias} dias`;
  return <Chip size="small" color={dias < 3 ? "error" : dias < 7 ? "warning" : "default"} variant={dias < 7 ? "filled" : "outlined"} label={rotulo} />;
}

/* ------------------------------------ Itens (consulta na hora) ------------------------------------ */

export function DialogoItens({ resultado, termos, aoFechar }: { resultado: Resultado | null; termos: string[]; aoFechar: () => void }) {
  const [itens, setItens] = useState<ItemPncp[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const celular = useCelular();
  useEffect(() => {
    setItens(null);
    setErro(null);
    const controle = resultado ? lerControlePncp(resultado.numero_controle_pncp) : null;
    if (!controle) return;
    let ativo = true;
    consultarItens(controle)
      .then((i) => ativo && setItens(i))
      .catch((e: Error) => ativo && setErro(e.message));
    return () => {
      ativo = false;
    };
  }, [resultado?.numero_controle_pncp]);
  if (!resultado) return null;
  const comPalavra = (itens ?? []).filter((i) => trechosDestacados(i.descricao, termos).some((t) => t.destaque)).length;
  return (
    <Dialog open onClose={aoFechar} fullWidth maxWidth="lg" fullScreen={celular}>
      <DialogTitle>
        Itens — {resultado.orgao_nome} {resultado.numero}
      </DialogTitle>
      <DialogContent>
        {erro && <Alert severity="warning">{erro}</Alert>}
        {!itens && !erro && <CircularProgress size={24} />}
        {itens && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {itens.length} item(ns){termos.length ? ` · ${comPalavra} com as palavras da busca` : ""}
            </Typography>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell align="right">Qtd.</TableCell>
                    <TableCell>Unid.</TableCell>
                    <TableCell align="right">Valor unit. est.</TableCell>
                    <TableCell>ME/EPP</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itens.map((i) => (
                    <TableRow key={i.numeroItem}>
                      <TableCell>{i.numeroItem}</TableCell>
                      <TableCell sx={{ maxWidth: 560 }}>
                        <Typography variant="body2">
                          <Destacado texto={i.descricao} termos={termos} />
                        </Typography>
                        {situacaoFoge(i.situacaoCompraItemNome) && (
                          <Chip size="small" color="warning" variant="outlined" label={i.situacaoCompraItemNome} sx={{ mt: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">{formatarNumero(i.quantidade)}</TableCell>
                      <TableCell>{i.unidadeMedida ?? ""}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {i.orcamentoSigiloso ? "Sigiloso" : formatarMoeda(i.valorUnitarioEstimado)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{BENEFICIOS.find((b) => b.id === beneficioDoPncp(i.tipoBeneficio))?.name ?? "—"}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ------------------------------------ Página ------------------------------------ */

export function BuscaPncp() {
  const notify = useNotify();
  const navigate = useNavigate();
  const { data: filtros = [], refetch: recarregarFiltros } = useGetList<Filtro>("busca_filtro", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "nome", order: "ASC" },
  });
  const { data: modalidades = [] } = useGetList<{ id: string; nome: string; codigos_pncp: number[] }>("modalidade", {
    filter: { ativo: true },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "nome", order: "ASC" },
  });
  const [filtroId, setFiltroId] = useState<string | null>(null);
  const [ufs, setUfs] = useState<string[]>([]);
  const [codigos, setCodigos] = useState<number[]>([]);
  const [palavras, setPalavras] = useState<string[]>([]);
  const [textoPalavra, setTextoPalavra] = useState("");
  const [esferas, setEsferas] = useState<string[]>([]);
  const [sistemas, setSistemas] = useState<string[]>([]);
  const [encerraDe, setEncerraDe] = useState("");
  const [encerraAte, setEncerraAte] = useState("");
  const [publicadaDesde, setPublicadaDesde] = useState("");
  const [soFavoritas, setSoFavoritas] = useState(false);
  const [ordem, setOrdem] = useState<Ordem>("encerramento");
  const [maisFiltros, setMaisFiltros] = useState(false);
  const [opcoesSistema, setOpcoesSistema] = useState<{ sistema: string; abertas: number }[]>([]);
  const [anotando, setAnotando] = useState<Resultado | null>(null);
  const [textoAnotacao, setTextoAnotacao] = useState("");
  const [visitaAnterior, setVisitaAnterior] = useState<string | null>(null);
  const [mostrarIgnoradas, setMostrarIgnoradas] = useState(false);
  const [soNovas, setSoNovas] = useState(false);
  const [resultados, setResultados] = useState<Resultado[] | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [ultimaColeta, setUltimaColeta] = useState<Coleta | null>(null);
  const [coletando, setColetando] = useState<string | null>(null);
  const [itensDe, setItensDe] = useState<Resultado | null>(null);
  const [cadastrando, setCadastrando] = useState<string | null>(null);
  const [progresso, setProgresso] = useState("");
  // Salvar filtro: atualizar um existente ou criar um novo (pergunta qual).
  const [salvando, setSalvando] = useState<{ destino: string; nome: string } | null>(null);
  const [mostrarCadastradas, setMostrarCadastradas] = useState(false);
  const consulta = useRef(0);
  // Pesquisa livre (D110): o texto do campo de cima alimenta a mesma lista, direto no índice do PNCP (todo o país).
  const [pesquisa, setPesquisa] = useState("");
  const [situacaoPesquisa, setSituacaoPesquisa] = useState<SituacaoPesquisa>("recebendo_proposta");
  const [achados, setAchados] = useState<Resultado[] | null>(null);
  const [totalPesquisa, setTotalPesquisa] = useState(0);
  const [paginaPesquisa, setPaginaPesquisa] = useState(1);
  const [pesquisando, setPesquisando] = useState(false);
  const [erroPesquisa, setErroPesquisa] = useState<string | null>(null);
  const [ordemPesquisa, setOrdemPesquisa] = useState<Ordem>("relevancia");
  const consultaPesquisa = useRef(0);
  // Achados como vieram do PNCP: viram linha da cópia local quando a equipe marca ou anota.
  const brutos = useRef(new Map<string, AchadoPncp>());
  const termoPesquisa = pesquisa.trim();
  const controlePesquisa = useMemo(() => lerControlePncp(termoPesquisa), [termoPesquisa]);
  const modoPesquisa = !controlePesquisa && termoPesquisa.length >= 3;
  // No celular, durante a pesquisa, esconde o que não se aplica para a lista ficar à vista.
  const celular = useCelular();
  const ocultarDaColeta = modoPesquisa && celular;

  // Abre o primeiro filtro salvo; ao abrir um filtro, guarda a visita anterior (para marcar as novas) e registra a atual.
  function abrirFiltro(f: Filtro) {
    setFiltroId(f.id);
    setUfs(f.ufs);
    setCodigos(f.modalidades);
    setPalavras(f.palavras);
    setEsferas(f.esferas ?? []);
    setSistemas(f.sistemas ?? []);
    setVisitaAnterior(f.ultima_visita);
    void supabase.from("busca_filtro").update({ ultima_visita: new Date().toISOString() }).eq("id", f.id);
  }
  useEffect(() => {
    if (!filtroId && filtros[0]) abrirFiltro(filtros[0]);
  }, [filtros, filtroId]);

  async function lerUltimaColeta() {
    const { data } = await supabase.from("pncp_coleta").select("*").not("concluida_em", "is", null).order("iniciada_em", { ascending: false }).limit(1);
    setUltimaColeta(((data ?? [])[0] as Coleta | undefined) ?? null);
  }
  useEffect(() => {
    void lerUltimaColeta();
    void supabase.rpc("sistemas_pncp").then(({ data }) => setOpcoesSistema((data ?? []) as { sistema: string; abertas: number }[]));
  }, []);

  async function buscar() {
    const minha = ++consulta.current;
    setCarregando(true);
    const { data, error } = await supabase.rpc("buscar_pncp", {
      ufs,
      modalidades: codigos,
      palavras,
      mostrar_ignoradas: mostrarIgnoradas,
      esferas,
      sistemas,
      encerra_de: encerraDe || null,
      encerra_ate: encerraAte || null,
      publicada_desde: publicadaDesde || null,
      so_favoritas: soFavoritas,
    });
    if (minha !== consulta.current) return;
    setCarregando(false);
    if (error) {
      notify(`Não foi possível buscar: ${error.message}`, { type: "error" });
      return;
    }
    setResultados((data ?? []) as Resultado[]);
  }
  // Busca sozinha quando os filtros mudam (D46).
  useEffect(() => {
    if (!filtroId) return;
    const t = setTimeout(() => void buscar(), 300);
    return () => clearTimeout(t);
  }, [filtroId, ufs.join(), codigos.join(), palavras.join("|"), mostrarIgnoradas, esferas.join(), sistemas.join("|"), encerraDe, encerraAte, publicadaDesde, soFavoritas]);

  async function pesquisar(pagina: number) {
    const minha = ++consultaPesquisa.current;
    setPesquisando(true);
    setErroPesquisa(null);
    if (pagina === 1) setAchados(null);
    try {
      const { total, itens } = await pesquisarPncp(termoPesquisa, situacaoPesquisa, ufs, codigos, pagina);
      const marcas = await marcasDe(itens.map((a) => a.numero_controle_pncp));
      if (minha !== consultaPesquisa.current) return;
      for (const a of itens) brutos.current.set(a.numero_controle_pncp, a);
      const novos = itens.map((a) => resultadoDoAchado(a, marcas));
      setAchados((atual) => {
        if (pagina === 1 || !atual) return novos;
        const ja = new Set(atual.map((x) => x.numero_controle_pncp));
        return [...atual, ...novos.filter((n) => !ja.has(n.numero_controle_pncp))];
      });
      setTotalPesquisa(total);
      setPaginaPesquisa(pagina);
    } catch (e) {
      if (minha === consultaPesquisa.current) setErroPesquisa((e as Error).message);
    } finally {
      if (minha === consultaPesquisa.current) setPesquisando(false);
    }
  }
  // Pesquisa sozinha ao digitar (D46), com UFs e modalidades do painel.
  useEffect(() => {
    if (!modoPesquisa) {
      consultaPesquisa.current++;
      setAchados(null);
      setPesquisando(false);
      setErroPesquisa(null);
      return;
    }
    const t = setTimeout(() => void pesquisar(1), 500);
    return () => clearTimeout(t);
  }, [modoPesquisa, termoPesquisa, situacaoPesquisa, ufs.join(), codigos.join()]);

  /** Troca um item na lista ativa (pesquisa ou coleta). */
  function atualizarNaLista(controle: string, mudar: (r: Resultado) => Resultado | null) {
    const aplicar = (lista: Resultado[] | null) =>
      lista &&
      lista.flatMap((x) => {
        if (x.numero_controle_pncp !== controle) return [x];
        const novo = mudar(x);
        return novo ? [novo] : [];
      });
    if (modoPesquisa) setAchados(aplicar);
    else setResultados(aplicar);
  }

  /** Achado da pesquisa entra na cópia local antes de marcar, para aparecer em "Só novas oportunidades". */
  async function garantirNaCopia(r: Resultado): Promise<string | null> {
    const a = brutos.current.get(r.numero_controle_pncp);
    if (!modoPesquisa || !a) return null;
    const { error } = await supabase.from("pncp_contratacao").upsert(linhaDoAchado(a), { onConflict: "numero_controle_pncp", ignoreDuplicates: true });
    return error ? error.message : null;
  }

  const filtroAtual = filtros.find((f) => f.id === filtroId) ?? null;
  const alterado =
    filtroAtual !== null &&
    (filtroAtual.ufs.join() !== ufs.join() ||
      filtroAtual.modalidades.join() !== codigos.join() ||
      filtroAtual.palavras.join("|") !== palavras.join("|") ||
      (filtroAtual.esferas ?? []).join() !== esferas.join() ||
      (filtroAtual.sistemas ?? []).join("|") !== sistemas.join("|"));
  const ehNova = (r: Resultado) => Boolean(visitaAnterior && r.primeira_coleta_em && new Date(r.primeira_coleta_em) > new Date(visitaAnterior));
  // Na pesquisa, a lista mostra tudo o que o PNCP achou (captadas e ignoradas com o selo), na ordem escolhida.
  const visiveis = useMemo(
    () =>
      modoPesquisa
        ? ordenar(achados ?? [], ordemPesquisa)
        : ordenar((resultados ?? []).filter((r) => (!soNovas || ehNova(r)) && (mostrarCadastradas || soFavoritas || !r.licitacao_id)), ordem),
    [modoPesquisa, achados, ordemPesquisa, resultados, soNovas, visitaAnterior, ordem, mostrarCadastradas, soFavoritas],
  );
  // Destaque no objeto: palavras da pesquisa ou palavras-chave do filtro.
  const termosDestaque = useMemo(
    () => (modoPesquisa ? termoPesquisa.split(/\s+/).filter((w) => w.length >= 3) : palavras),
    [modoPesquisa, termoPesquisa, palavras],
  );
  const filtrosExtras = [esferas.length, sistemas.length, encerraDe, encerraAte, publicadaDesde].filter(Boolean).length;
  const novas = (resultados ?? []).filter(ehNova).length;
  const cadastradasOcultas = mostrarCadastradas || soFavoritas ? 0 : (resultados ?? []).filter((r) => r.licitacao_id).length;
  // Palavras já usadas nos filtros salvos, para adicionar com um clique.
  const sugestoes = useMemo(
    () => [...new Set(filtros.flatMap((f) => f.palavras))].filter((p) => !palavras.includes(p)).sort((a, b) => a.localeCompare(b, "pt-BR")),
    [filtros, palavras],
  );
  const modalidadesEscolhidas = modalidades.filter((m) => m.codigos_pncp.length > 0 && m.codigos_pncp.every((c) => codigos.includes(c)));

  async function salvarFiltro() {
    if (!salvando) return;
    const dados = { ufs, modalidades: codigos, palavras, esferas, sistemas };
    if (salvando.destino === "novo") {
      const nome = salvando.nome.trim();
      const { data, error } = await supabase
        .from("busca_filtro")
        .insert({ nome, ...dados, ultima_visita: new Date().toISOString() })
        .select("*")
        .single();
      if (error) {
        notify(`Não foi possível salvar: ${error.message}`, { type: "error" });
        return;
      }
      notify(`Filtro "${nome}" criado.`, { type: "success" });
      await recarregarFiltros();
      setFiltroId((data as Filtro).id);
    } else {
      const { error } = await supabase.from("busca_filtro").update(dados).eq("id", salvando.destino);
      if (error) {
        notify(`Não foi possível salvar: ${error.message}`, { type: "error" });
        return;
      }
      notify(`Filtro "${filtros.find((f) => f.id === salvando.destino)?.nome ?? ""}" atualizado.`, { type: "success" });
      await recarregarFiltros();
      setFiltroId(salvando.destino);
    }
    setSalvando(null);
  }

  /** Chama a Edge Function pncp-coletar e devolve o corpo, com a mensagem de erro do servidor quando houver. */
  async function etapaColeta<T>(corpo: Record<string, unknown>): Promise<T> {
    const { data, error } = await supabase.functions.invoke<T & { ok?: boolean; erro?: string }>("pncp-coletar", { body: corpo });
    if (error || !data?.ok) {
      let mensagem = error?.message ?? data?.erro ?? "falha";
      const contexto = (error as { context?: Response } | null)?.context;
      if (contexto) mensagem = ((await contexto.json().catch(() => null)) as { erro?: string } | null)?.erro ?? mensagem;
      throw new Error(mensagem);
    }
    return data;
  }

  // Coleta em etapas (limite de 150 s por chamada da Edge Function): UF por UF, em blocos de páginas.
  async function atualizarAgora() {
    setColetando("Iniciando…");
    let registros = 0;
    let novasColeta = 0;
    const falhas: string[] = [];
    try {
      const inicio = await etapaColeta<{ coleta_id: string; ufs: string[] }>({ acao: "iniciar" });
      const coletaId = inicio.coleta_id;
      for (const uf of inicio.ufs) {
        try {
          let pagina: number | null = 1;
          while (pagina !== null) {
            setColetando(`Atualizando ${uf}${pagina > 1 ? ` (página ${pagina})` : ""}…`);
            const bloco: { registros: number; novas: number; proxima_pagina: number | null } = await etapaColeta({ acao: "bloco", uf, pagina });
            registros += bloco.registros;
            novasColeta += bloco.novas;
            pagina = bloco.proxima_pagina;
          }
        } catch (e) {
          falhas.push(`${uf}: ${(e as Error).message}`);
        }
      }
      await etapaColeta({ acao: "concluir", coleta_id: coletaId, registros, novas: novasColeta, erro: falhas.join("; ") || null });
      notify(
        falhas.length
          ? `PNCP atualizado em parte: ${registros} contratações, ${novasColeta} novas. Falharam: ${falhas.join("; ")}`
          : `PNCP atualizado: ${registros} contratações abertas, ${novasColeta} novas.`,
        { type: falhas.length ? "warning" : "success" },
      );
    } catch (e) {
      notify(`Não foi possível atualizar: ${(e as Error).message}`, { type: "error" });
    } finally {
      setColetando(null);
      await lerUltimaColeta();
      void buscar();
    }
  }

  async function alternarIgnorada(r: Resultado) {
    const { error } = r.ignorada
      ? await supabase.from("busca_ignorada").delete().eq("numero_controle_pncp", r.numero_controle_pncp)
      : await supabase.from("busca_ignorada").insert({ numero_controle_pncp: r.numero_controle_pncp });
    if (error) notify(`Não foi possível alterar: ${error.message}`, { type: "error" });
    else if (modoPesquisa) atualizarNaLista(r.numero_controle_pncp, (x) => ({ ...x, ignorada: !r.ignorada }));
    else void buscar();
  }

  // Favoritar/desfavoritar atualiza a linha na hora (uso no celular, rolando a lista).
  async function alternarFavorita(r: Resultado) {
    if (!r.favorita) {
      const falha = await garantirNaCopia(r);
      if (falha) {
        notify(`Não foi possível marcar: ${falha}`, { type: "error" });
        return;
      }
    }
    const { error } = r.favorita
      ? await supabase.from("busca_favorita").delete().eq("numero_controle_pncp", r.numero_controle_pncp)
      : await supabase.from("busca_favorita").insert({ numero_controle_pncp: r.numero_controle_pncp });
    if (error) {
      notify(`Não foi possível alterar: ${error.message}`, { type: "error" });
      return;
    }
    atualizarNaLista(r.numero_controle_pncp, (x) =>
      !modoPesquisa && soFavoritas && r.favorita ? null : { ...x, favorita: !r.favorita, anotacao: r.favorita ? null : x.anotacao },
    );
  }

  async function salvarAnotacao() {
    if (!anotando) return;
    const anotacao = textoAnotacao.trim() || null;
    const falha = await garantirNaCopia(anotando);
    if (falha) {
      notify(`Não foi possível salvar: ${falha}`, { type: "error" });
      return;
    }
    const { error } = await supabase
      .from("busca_favorita")
      .upsert({ numero_controle_pncp: anotando.numero_controle_pncp, anotacao }, { onConflict: "numero_controle_pncp" });
    if (error) {
      notify(`Não foi possível salvar: ${error.message}`, { type: "error" });
      return;
    }
    atualizarNaLista(anotando.numero_controle_pncp, (x) => ({ ...x, favorita: true, anotacao }));
    setAnotando(null);
  }

  async function cadastrar(r: Resultado) {
    const controle = lerControlePncp(r.numero_controle_pncp);
    if (!controle) return;
    setCadastrando(r.numero_controle_pncp);
    try {
      const { id, itens, existente, preparacao } = await cadastrarDoPncp(controle, setProgresso);
      notify(
        existente
          ? "Esta licitação já estava cadastrada."
          : `Licitação captada com ${itens} item(ns).${preparacao ? ` ${textoDaPreparacao(preparacao)}.` : ""} Confira a data da sessão e os prazos.`,
        { type: existente ? "info" : "success", autoHideDuration: 12000 },
      );
      navigate(`/licitacao/${id}`);
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setCadastrando(null);
    }
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Title title="Buscar licitações" />
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }}>
                <TextField
                  size="small"
                  label="Pesquisar no PNCP"
                  placeholder="Qualquer coisa: produto, órgão, cidade, número — ou cole o link do edital"
                  value={pesquisa}
                  onChange={(e) => setPesquisa(e.target.value)}
                  sx={{ flex: 1, minWidth: 0 }}
                  helperText={
                    controlePesquisa
                      ? "Link ou número de controle reconhecido: a licitação aparece abaixo."
                      : modoPesquisa
                        ? "Pesquisa no PNCP inteiro (objeto, órgão e itens). Valem as UFs e modalidades escolhidas; palavras-chave e mais filtros são da lista coletada."
                        : "Vazio: lista coletada com os filtros abaixo. Com 3 letras ou mais, a lista passa a mostrar o que o PNCP encontrar."
                  }
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          {pesquisando ? <CircularProgress size={18} /> : <PesquisaIcon fontSize="small" />}
                        </InputAdornment>
                      ),
                      endAdornment: pesquisa ? (
                        <InputAdornment position="end">
                          <Button size="small" onClick={() => setPesquisa("")}>
                            Limpar
                          </Button>
                        </InputAdornment>
                      ) : undefined,
                    },
                  }}
                />
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={situacaoPesquisa}
                  onChange={(_, v: SituacaoPesquisa | null) => v && setSituacaoPesquisa(v)}
                  disabled={!modoPesquisa}
                  sx={{ flexShrink: 0, height: 40 }}
                >
                  <ToggleButton value="recebendo_proposta">Abertas</ToggleButton>
                  <ToggleButton value="todos">Todas</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              {controlePesquisa && (
                <Box sx={{ mt: 1.5 }}>
                  <CadastroPeloPncp textoExterno={termoPesquisa} rotuloBotao="Captar licitação com os itens e arquivos" />
                </Box>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 4 }} sx={{ display: ocultarDaColeta ? "none" : undefined }}>
              <TextField
                select
                size="small"
                label={alterado ? "Filtro salvo (modificado)" : "Filtro salvo"}
                value={alterado ? "" : (filtroId ?? "")}
                onChange={(e) => {
                  const f = filtros.find((x) => x.id === e.target.value);
                  if (f) abrirFiltro(f);
                }}
                fullWidth
              >
                {filtros.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.nome}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                multiple
                size="small"
                options={UFS.map((u) => u.id)}
                value={ufs}
                onChange={(_, v) => setUfs(v)}
                renderInput={(params) => <TextField {...params} label="UFs" placeholder={ufs.length ? "" : modoPesquisa ? "Todo o país" : "Todas as coletadas"} />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                multiple
                size="small"
                options={modalidades.filter((m) => m.codigos_pncp.length > 0)}
                getOptionLabel={(m) => m.nome}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={modalidadesEscolhidas}
                onChange={(_, v) => setCodigos(v.flatMap((m) => m.codigos_pncp))}
                renderInput={(params) => <TextField {...params} label="Modalidades" placeholder={codigos.length ? "" : "Todas"} />}
              />
            </Grid>
            <Grid size={12} sx={{ display: ocultarDaColeta ? "none" : undefined }}>
              <Autocomplete
                multiple
                freeSolo
                autoSelect
                disabled={modoPesquisa}
                size="small"
                options={[]}
                value={palavras}
                inputValue={textoPalavra}
                onInputChange={(_, v, motivo) => motivo !== "reset" && setTextoPalavra(v)}
                onChange={(_, v) => {
                  setPalavras([...new Set((v as string[]).map((p) => p.trim()).filter(Boolean))]);
                  setTextoPalavra("");
                }}
                renderValue={(valores, getItemProps) =>
                  valores.map((p, indice) => {
                    const { key, ...props } = getItemProps({ index: indice });
                    return (
                      <Chip
                        key={key}
                        {...props}
                        size="small"
                        label={p}
                        onClick={() => {
                          // Clicar na palavra a devolve ao campo para editar.
                          setPalavras((atuais) => atuais.filter((x) => x !== p));
                          setTextoPalavra(p);
                        }}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Palavras-chave no objeto"
                    placeholder={palavras.length ? "Digite e tecle Enter" : "Sem palavras: todas as contratações"}
                    helperText="Clique numa palavra para editar. Cada palavra casa com o início de uma palavra do objeto, sem acento (“eletronic” encontra eletrônicos)."
                  />
                )}
              />
              {sugestoes.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                    Adicionar:
                  </Typography>
                  {sugestoes.map((p) => (
                    <Chip key={p} size="small" variant="outlined" icon={<AddIcon />} label={p} onClick={() => setPalavras((atuais) => [...atuais, p])} />
                  ))}
                </Stack>
              )}
            </Grid>
            <Grid size={12}>
              <Collapse in={maisFiltros} unmountOnExit={false}>
                <Grid container spacing={2} sx={{ pt: 0.5 }}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={ESFERAS_PNCP.map((e) => e.id)}
                      getOptionLabel={(e: string) => nomeEsfera(e)}
                      value={esferas}
                      onChange={(_, v) => setEsferas(v)}
                      renderInput={(params) => <TextField {...params} label="Esfera do órgão" placeholder={esferas.length ? "" : "Todas"} />}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={opcoesSistema.map((o) => o.sistema)}
                      getOptionLabel={(s) => {
                        const o = opcoesSistema.find((x) => x.sistema === s);
                        return o ? `${s} (${o.abertas})` : s;
                      }}
                      value={sistemas}
                      onChange={(_, v) => setSistemas(v)}
                      renderInput={(params) => (
                        <TextField {...params} label="Sistema de disputa" placeholder={sistemas.length ? "" : "Todos"} helperText="Sistema que publicou no PNCP (onde a disputa acontece)." />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <TextField size="small" type="date" label="Encerra a partir de" value={encerraDe} onChange={(e) => setEncerraDe(e.target.value)} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
                  </Grid>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <TextField size="small" type="date" label="Encerra até" value={encerraAte} onChange={(e) => setEncerraAte(e.target.value)} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField size="small" type="date" label="Publicadas desde" value={publicadaDesde} onChange={(e) => setPublicadaDesde(e.target.value)} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
                  </Grid>
                </Grid>
              </Collapse>
            </Grid>
          </Grid>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ md: "center" }}
            sx={{ mt: 1, display: ocultarDaColeta ? "none" : undefined }}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Button size="small" startIcon={<FiltroIcon />} onClick={() => setMaisFiltros((m) => !m)} disabled={modoPesquisa}>
                {maisFiltros ? "Menos filtros" : `Mais filtros${filtrosExtras ? ` (${filtrosExtras})` : ""}`}
              </Button>
              <FormControlLabel
                control={<Switch disabled={modoPesquisa} checked={soFavoritas} onChange={(e) => setSoFavoritas(e.target.checked)} />}
                label="Só novas oportunidades"
              />
              <FormControlLabel control={<Switch disabled={modoPesquisa} checked={soNovas} onChange={(e) => setSoNovas(e.target.checked)} />} label="Só as novas" />
              <FormControlLabel control={<Switch disabled={modoPesquisa} checked={mostrarIgnoradas} onChange={(e) => setMostrarIgnoradas(e.target.checked)} />} label="Mostrar ignoradas" />
              <FormControlLabel
                control={<Switch disabled={modoPesquisa} checked={mostrarCadastradas} onChange={(e) => setMostrarCadastradas(e.target.checked)} />}
                label="Mostrar captadas"
              />
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant={alterado ? "contained" : "outlined"}
                startIcon={<SaveIcon />}
                onClick={() => setSalvando({ destino: filtroAtual?.id ?? "novo", nome: "" })}
              >
                Salvar filtro
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {modoPesquisa
            ? pesquisando && !achados
              ? "Pesquisando no PNCP…"
              : achados
                ? `${visiveis.length} de ${totalPesquisa} encontrada(s) no PNCP para “${termoPesquisa}”${situacaoPesquisa === "recebendo_proposta" ? " com propostas abertas" : ""}`
                : ""
            : carregando
            ? "Buscando…"
            : resultados
              ? `${visiveis.length} contratação(ões)${soFavoritas ? " marcadas como nova oportunidade" : " com propostas abertas"}${novas ? ` · ${novas} nova(s) desde a última visita` : ""}${cadastradasOcultas ? ` · ${cadastradasOcultas} já captada(s) oculta(s)` : ""}`
              : ""}
        </Typography>
        <TextField
          select
          size="small"
          label="Ordenar por"
          value={modoPesquisa ? ordemPesquisa : ordem}
          onChange={(e) => (modoPesquisa ? setOrdemPesquisa : setOrdem)(e.target.value as Ordem)}
          sx={{ minWidth: 220 }}
        >
          {ORDENS.filter((o) => modoPesquisa || o.id !== "relevancia").map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.nome}
            </MenuItem>
          ))}
        </TextField>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {ultimaColeta
              ? `PNCP atualizado em ${formatarDataHora(ultimaColeta.concluida_em)}${ultimaColeta.erro ? " (com erro)" : ""}`
              : "Ainda sem coleta"}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={coletando ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => void atualizarAgora()}
            disabled={coletando !== null}
          >
            {coletando ?? "Atualizar agora"}
          </Button>
        </Stack>
      </Stack>
      {ultimaColeta?.erro && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          A última coleta não terminou: {ultimaColeta.erro}
        </Alert>
      )}
      {modoPesquisa && erroPesquisa && (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            <Button size="small" color="inherit" onClick={() => void pesquisar(1)}>
              Tentar de novo
            </Button>
          }
        >
          {erroPesquisa}
        </Alert>
      )}
      {modoPesquisa && achados && achados.length === 0 && !pesquisando && (
        <Alert severity="info">
          Nada encontrado no PNCP para “{termoPesquisa}”{situacaoPesquisa === "recebendo_proposta" ? " com propostas abertas. Experimente “Todas”." : "."}
        </Alert>
      )}
      {!modoPesquisa && resultados && resultados.length === 0 && !carregando && (
        <Alert severity="info">
          {ultimaColeta ? "Nenhuma contratação aberta com esses filtros." : "Ainda não há contratações coletadas. Use “Atualizar agora”."}
        </Alert>
      )}

      <Stack spacing={1}>
        {visiveis.map((r) => {
          const link = linkPncp(r.numero_controle_pncp);
          return (
            <Card key={r.numero_controle_pncp} variant="outlined" sx={{ opacity: r.ignorada ? 0.55 : 1 }}>
              <CardContent sx={{ pb: "12px !important" }}>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 0.5 }}>
                  <Tooltip title={r.favorita ? "Tirar das novas oportunidades" : "Marcar como nova oportunidade"}>
                    <IconButton
                      size="small"
                      onClick={() => void alternarFavorita(r)}
                      aria-label={r.favorita ? "Tirar das novas oportunidades" : "Marcar como nova oportunidade"}
                      sx={{ color: r.favorita ? "warning.main" : "text.secondary", p: 0.5 }}
                    >
                      {r.favorita ? <EstrelaIcon /> : <EstrelaVaziaIcon />}
                    </IconButton>
                  </Tooltip>
                  <ChipPrazo fim={r.data_encerramento_proposta} />
                  {ehNova(r) && <Chip size="small" color="secondary" label="Nova" />}
                  {r.licitacao_id && <Chip size="small" color="success" variant="outlined" label="Captada" />}
                  {r.ignorada && <Chip size="small" variant="outlined" label="Ignorada" />}
                  <Typography variant="caption" color="text.secondary">
                    {r.modalidade_nome} {r.numero}
                    {r.srp ? " · Registro de preços" : ""} · {r.municipio ? `${r.municipio}/` : ""}
                    {r.uf} · {nomeEsfera(r.esfera)}
                    {r.sistema ? ` · ${r.sistema}` : ""}
                  </Typography>
                </Stack>
                <Typography variant="subtitle2">{r.orgao_nome}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  <Destacado texto={r.objeto} termos={termosDestaque} />
                </Typography>
                {r.favorita && r.anotacao && (
                  <Typography variant="body2" sx={{ mt: 0.5, px: 1, py: 0.5, bgcolor: "action.hover", borderRadius: 1 }}>
                    {r.anotacao}
                  </Typography>
                )}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Valor estimado {r.valor_total_estimado ? formatarMoeda(r.valor_total_estimado) : "sigiloso ou não informado"} · propostas até{" "}
                    {formatarDataHora(r.data_encerramento_proposta)}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Button size="small" onClick={() => setItensDe(r)}>
                      Ver itens
                    </Button>
                    <Button
                      size="small"
                      startIcon={<NotaIcon />}
                      onClick={() => {
                        setAnotando(r);
                        setTextoAnotacao(r.anotacao ?? "");
                      }}
                    >
                      Anotar
                    </Button>
                    {link && (
                      <Button size="small" component={Link} href={link} target="_blank" rel="noopener noreferrer">
                        PNCP
                      </Button>
                    )}
                    <Button size="small" onClick={() => void alternarIgnorada(r)}>
                      {r.ignorada ? "Restaurar" : "Ignorar"}
                    </Button>
                    {r.licitacao_id ? (
                      <Button size="small" variant="outlined" onClick={() => navigate(`/licitacao/${r.licitacao_id}`)}>
                        Abrir licitação
                      </Button>
                    ) : (
                      <Button size="small" variant="contained" disabled={cadastrando !== null} onClick={() => void cadastrar(r)}>
                        {cadastrando === r.numero_controle_pncp ? progresso || "Captando…" : "Captar"}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {modoPesquisa && achados && achados.length < totalPesquisa && (
        <Box sx={{ textAlign: "center", mt: 1.5 }}>
          <Button variant="outlined" onClick={() => void pesquisar(paginaPesquisa + 1)} disabled={pesquisando} startIcon={pesquisando ? <CircularProgress size={16} /> : undefined}>
            Mostrar mais ({totalPesquisa - achados.length} restante(s))
          </Button>
        </Box>
      )}

      <DialogoItens resultado={itensDe} termos={termosDestaque} aoFechar={() => setItensDe(null)} />
      <Dialog open={anotando !== null} onClose={() => setAnotando(null)} fullWidth maxWidth="sm">
        <DialogTitle>Anotação da oportunidade</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {anotando?.orgao_nome} — {anotando?.numero}. Anotar também marca como nova oportunidade.
          </Typography>
          <TextField
            autoFocus
            label="Anotação"
            value={textoAnotacao}
            onChange={(e) => setTextoAnotacao(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            placeholder="Ex.: conferir catálogo do item 3; pedir cotação ao fornecedor X"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnotando(null)}>Cancelar</Button>
          <Button variant="contained" onClick={() => void salvarAnotacao()}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={salvando !== null} onClose={() => setSalvando(null)} fullWidth maxWidth="xs">
        <DialogTitle>Salvar filtro</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              label="Salvar em"
              value={salvando?.destino ?? "novo"}
              onChange={(e) => setSalvando((atual) => (atual ? { ...atual, destino: e.target.value } : atual))}
              fullWidth
            >
              <MenuItem value="novo">Novo filtro</MenuItem>
              {filtros.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  Atualizar “{f.nome}”
                </MenuItem>
              ))}
            </TextField>
            {salvando?.destino === "novo" && (
              <TextField
                autoFocus
                label="Nome do novo filtro"
                value={salvando.nome}
                onChange={(e) => setSalvando((atual) => (atual ? { ...atual, nome: e.target.value } : atual))}
                fullWidth
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSalvando(null)}>Cancelar</Button>
          <Button variant="contained" disabled={salvando?.destino === "novo" && !salvando.nome.trim()} onClick={() => void salvarFiltro()}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
